<template>
  <div class="p-4">

    <!-- ============================================================ -->
    <!-- ==================== VUE SELLER / PARTNER ================== -->
    <!-- ============================================================ -->
    <div v-if="isSellerPartner">

      <!-- ===== Programme INACTIF => on cache tout, message "bientôt" ===== -->
      <div v-if="loaded && !(info.gift && info.gift.active)"
           class="flex flex-col items-center justify-center py-20 px-6 bg-white rounded-2xl shadow border border-gray-100 text-center">
        <div class="flex items-center justify-center rounded-full mb-5" style="width:88px;height:88px;background:linear-gradient(135deg,#e8f9f0,#d2f3e2);">
          <i class="material-icons text-green-500" style="font-size:44px;">card_giftcard</i>
        </div>
        <h2 class="text-2xl font-bold text-gray-800">Programme de parrainage</h2>
        <p class="text-gray-500 mt-2" style="max-width:440px;">
          Le programme n'est pas encore activé. Bientôt, vous pourrez inviter vos amis et
          recevoir un cadeau dans votre portefeuille à chaque filleul 🎁
        </p>
        <span class="mt-5 text-xs font-bold uppercase tracking-wide bg-amber-100 text-amber-700 px-4 py-1.5 rounded-full">
          ⏳ Bientôt disponible
        </span>
      </div>

      <!-- ===== Chargement ===== -->
      <div v-else-if="!loaded" class="text-center py-20 text-gray-400 text-sm">Chargement...</div>

      <!-- ===== Programme ACTIF => l'UI complète ===== -->
      <template v-else>

      <!-- Header gradient: code + lien -->
      <div class="rounded-2xl p-6 text-white shadow-lg mb-5" style="background:linear-gradient(135deg,#04b85e,#017a3e);">
        <div class="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div class="text-xs uppercase tracking-wide opacity-80">🎁 Programme de parrainage</div>
            <div class="text-2xl font-bold mt-1">Invitez vos amis, gagnez un cadeau</div>
            <div class="text-sm opacity-90 mt-1">
              Cadeau par filleul : <b>{{ myGiftLabel }}</b> crédité dans votre portefeuille dès sa 1ère expédition reçue.
            </div>
          </div>
          <i class="material-icons opacity-70" style="font-size:52px;">card_giftcard</i>
        </div>

        <!-- Code + lien -->
        <div class="mt-5 flex flex-wrap items-center gap-3">
          <div class="rounded-xl px-4 py-2 flex items-center gap-2" style="background:rgba(255,255,255,.18);backdrop-filter:blur(4px);">
            <span class="text-xs uppercase opacity-80">Code</span>
            <span class="font-bold text-lg tracking-wider">{{ info.referralCode || '...' }}</span>
            <button @click="copy(info.referralCode)" class="focus:outline-none opacity-90 hover:opacity-100" title="Copier le code">
              <i class="material-icons" style="font-size:18px;">content_copy</i>
            </button>
          </div>
          <div class="rounded-xl px-4 py-2 flex items-center gap-2 min-w-0" style="background:rgba(255,255,255,.18);backdrop-filter:blur(4px);max-width:100%;">
            <span class="text-xs uppercase opacity-80">Lien</span>
            <span class="text-sm truncate" style="max-width:340px;">{{ referralLink }}</span>
            <button @click="copy(referralLink)" class="focus:outline-none opacity-90 hover:opacity-100" title="Copier le lien">
              <i class="material-icons" style="font-size:18px;">content_copy</i>
            </button>
          </div>
          <button @click="regenerate" :disabled="loading" class="text-sm bg-white text-green-700 font-semibold rounded-xl px-4 py-2 hover:bg-green-50 focus:outline-none flex items-center gap-1">
            <i class="material-icons" style="font-size:17px;">refresh</i> Nouveau code
          </button>
        </div>
      </div>

      <!-- Stats cards -->
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
        <div class="bg-white rounded-xl shadow p-4 border border-gray-100">
          <div class="text-xs text-gray-400 uppercase">Filleuls</div>
          <div class="text-2xl font-bold text-gray-800">{{ st.totalReferrals || 0 }}</div>
        </div>
        <div class="bg-white rounded-xl shadow p-4 border border-gray-100">
          <div class="text-xs text-gray-400 uppercase">En attente</div>
          <div class="text-2xl font-bold text-amber-500">{{ st.pendingReferrals || 0 }}</div>
        </div>
        <div class="bg-white rounded-xl shadow p-4 border border-gray-100">
          <div class="text-xs text-gray-400 uppercase">Récompensés</div>
          <div class="text-2xl font-bold text-green-600">{{ st.rewardedReferrals || 0 }}</div>
        </div>
        <div class="bg-white rounded-xl shadow p-4 border border-gray-100">
          <div class="text-xs text-gray-400 uppercase">Total gagné</div>
          <div class="text-2xl font-bold text-green-600">{{ myTotalEarned }}</div>
        </div>
      </div>

      <div class="grid md:grid-cols-2 gap-5">
        <!-- Filleuls -->
        <div class="bg-white rounded-xl shadow border border-gray-100">
          <div class="px-4 py-3 border-b border-gray-100 font-semibold text-gray-700 flex items-center gap-2">
            <i class="material-icons text-green-600" style="font-size:20px;">group</i> Mes filleuls
          </div>
          <div class="p-3">
            <div v-if="!info.referrals || !info.referrals.length" class="text-sm text-gray-400 text-center py-6">
              Aucun filleul pour le moment — partagez votre lien 🚀
            </div>
            <div v-for="(r, i) in info.referrals" :key="'ref'+i"
                 class="flex items-center justify-between py-2 px-2 rounded-lg hover:bg-gray-50 border-b border-gray-50">
              <div class="flex flex-col leading-tight">
                <span class="text-sm font-semibold text-gray-700">{{ r.fullName || r.email }}</span>
                <span class="text-xs text-gray-400">{{ r.referralDate ? $moment(r.referralDate).format('DD/MM/YY') : '-' }}</span>
              </div>
              <span class="text-xs px-2 py-0.5 rounded-full font-medium"
                    :class="r.affiliateStatus === 'rewarded' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'">
                {{ r.affiliateStatus === 'rewarded' ? '🎁 Récompensé' : 'En attente 1ère expédition' }}
              </span>
            </div>
          </div>
        </div>

        <!-- Rewards -->
        <div class="bg-white rounded-xl shadow border border-gray-100">
          <div class="px-4 py-3 border-b border-gray-100 font-semibold text-gray-700 flex items-center gap-2">
            <i class="material-icons text-green-600" style="font-size:20px;">card_giftcard</i> Mes cadeaux
          </div>
          <div class="p-3">
            <div v-if="!info.rewards || !info.rewards.length" class="text-sm text-gray-400 text-center py-6">
              Aucun cadeau pour le moment
            </div>
            <div v-for="(rw, i) in info.rewards" :key="'rw'+i"
                 class="flex items-center justify-between py-2 px-2 rounded-lg hover:bg-gray-50 border-b border-gray-50">
              <div class="flex flex-col leading-tight">
                <span class="text-sm font-semibold text-gray-700">{{ rw.referredUserName || 'Filleul' }}</span>
                <span class="text-xs text-gray-400">{{ rw.creditedAt ? $moment(rw.creditedAt).format('DD/MM/YY HH:mm') : ($moment(rw.createdAt).format('DD/MM/YY')) }}</span>
              </div>
              <div class="text-right">
                <div v-if="rw.status === 'credited'" class="text-sm font-bold text-green-600">
                  + {{ rw.currency === 'USD' ? (parseFloat(rw.amount) || 0).toFixed(2) + ' USD' : Math.floor(rw.amountMAD || 0) + ' MAD' }}
                </div>
                <span class="text-xs px-2 py-0.5 rounded-full font-medium"
                      :class="rw.status === 'credited' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'">
                  {{ rw.status === 'credited' ? 'Crédité' : 'En attente' }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      </template>
    </div>

    <!-- ============================================================ -->
    <!-- ========================= VUE ADMIN ======================== -->
    <!-- ============================================================ -->
    <div v-else>
      <div class="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div class="text-xl font-bold text-gray-800 flex items-center gap-2">
          <i class="material-icons text-green-600">card_giftcard</i> Programme de parrainage — Administration
        </div>
        <router-link to="/settings" class="text-sm text-green-600 font-semibold hover:underline flex items-center gap-1">
          <i class="material-icons" style="font-size:17px;">settings</i> Configurer le cadeau
        </router-link>
      </div>

      <!-- Bandeau config -->
      <div class="rounded-xl border px-4 py-3 mb-4 flex items-center gap-3 flex-wrap"
           :class="adminGift.active ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'">
        <i class="material-icons" :class="adminGift.active ? 'text-green-600' : 'text-amber-500'">{{ adminGift.active ? 'check_circle' : 'warning' }}</i>
        <span class="text-sm" :class="adminGift.active ? 'text-green-700' : 'text-amber-700'">
          <b>{{ adminGift.active ? 'Programme actif' : 'Programme inactif' }}</b>
          — Cadeau : {{ Math.floor(adminGift.amountMAD || 0) }} MAD / {{ (parseFloat(adminGift.amountUSD) || 0).toFixed(2) }} USD par filleul (1ère expédition reçue)
        </span>
      </div>

      <!-- Stats -->
      <div class="grid grid-cols-2 md:grid-cols-5 gap-4 mb-5">
        <div class="bg-white rounded-xl shadow p-4 border border-gray-100">
          <div class="text-xs text-gray-400 uppercase">Parrains</div>
          <div class="text-2xl font-bold text-gray-800">{{ adminStats.totalParrains || 0 }}</div>
        </div>
        <div class="bg-white rounded-xl shadow p-4 border border-gray-100">
          <div class="text-xs text-gray-400 uppercase">Filleuls</div>
          <div class="text-2xl font-bold text-gray-800">{{ adminStats.totalReferrals || 0 }}</div>
        </div>
        <div class="bg-white rounded-xl shadow p-4 border border-gray-100">
          <div class="text-xs text-gray-400 uppercase">En attente</div>
          <div class="text-2xl font-bold text-amber-500">{{ adminStats.pendingReferrals || 0 }}</div>
        </div>
        <div class="bg-white rounded-xl shadow p-4 border border-gray-100">
          <div class="text-xs text-gray-400 uppercase">Récompensés</div>
          <div class="text-2xl font-bold text-green-600">{{ adminStats.rewardedReferrals || 0 }}</div>
        </div>
        <div class="bg-white rounded-xl shadow p-4 border border-gray-100">
          <div class="text-xs text-gray-400 uppercase">Total cadeaux</div>
          <div class="text-lg font-bold text-green-600">{{ Math.floor(adminStats.totalGiftMAD || 0) }} MAD</div>
          <div class="text-xs text-gray-500 font-semibold">{{ (parseFloat(adminStats.totalGiftUSD) || 0).toFixed(2) }} USD</div>
        </div>
      </div>

      <!-- Liste des parrains -->
      <div class="bg-white rounded-xl shadow border border-gray-100">
        <div class="px-4 py-3 border-b border-gray-100 flex items-center justify-between flex-wrap gap-2">
          <span class="font-semibold text-gray-700">Parrains</span>
          <div class="relative">
            <input v-model="search" @keyup.enter="loadAdmin(1)" type="search" placeholder="Nom, email, code..."
                   class="border border-gray-200 rounded-lg pl-8 pr-3 py-1.5 text-sm outline-none focus:border-green-500" />
            <i class="material-icons absolute left-2 top-1.5 text-gray-400" style="font-size:18px;">search</i>
          </div>
        </div>
        <div style="overflow-x:auto;">
          <table class="min-w-full w-full text-sm">
            <thead>
              <tr class="bg-gray-50 text-gray-500 uppercase text-xs">
                <th class="px-4 py-2 text-left">Parrain</th>
                <th class="px-4 py-2 text-left">Code</th>
                <th class="px-4 py-2 text-center">Filleuls</th>
                <th class="px-4 py-2 text-center">En attente</th>
                <th class="px-4 py-2 text-center">Récompensés</th>
                <th class="px-4 py-2 text-right">Cadeaux reçus</th>
                <th class="px-4 py-2 text-center">Filleuls</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="p in adminList" :key="p._id" class="border-b border-gray-50 hover:bg-gray-50">
                <td class="px-4 py-2">
                  <div class="font-semibold text-gray-700">{{ p.fullName }} <span class="text-xs text-gray-400">({{ p.type }})</span></div>
                  <div class="text-xs text-gray-400">{{ p.email }}</div>
                </td>
                <td class="px-4 py-2 font-mono text-green-700 font-semibold">{{ p.referralCode || '-' }}</td>
                <td class="px-4 py-2 text-center font-semibold">{{ (p.stats && p.stats.totalReferrals) || 0 }}</td>
                <td class="px-4 py-2 text-center text-amber-600">{{ (p.stats && p.stats.pendingReferrals) || 0 }}</td>
                <td class="px-4 py-2 text-center text-green-600">{{ (p.stats && p.stats.rewardedReferrals) || 0 }}</td>
                <td class="px-4 py-2 text-right font-semibold text-green-700">
                  {{ p.currency === 'USD' ? ((parseFloat(p.stats && p.stats.totalGiftUSD) || 0).toFixed(2) + ' USD') : (Math.floor((p.stats && p.stats.totalGiftMAD) || 0) + ' MAD') }}
                </td>
                <td class="px-4 py-2 text-center">
                  <button @click="openParrainReferrals(p)" :disabled="!((p.stats && p.stats.totalReferrals) > 0)"
                    class="inline-flex items-center gap-1 text-xs font-semibold text-green-700 border border-green-200 rounded-full px-3 py-1 hover:bg-green-50 disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none">
                    <i class="material-icons" style="font-size:15px;">visibility</i> Voir
                  </button>
                </td>
              </tr>
              <tr v-if="!loading && !adminList.length">
                <td colspan="7" class="px-4 py-8 text-center text-gray-400">Aucun parrain pour le moment</td>
              </tr>
            </tbody>
          </table>
        </div>
        <!-- Pagination -->
        <div v-if="adminTotal > adminPerPage" class="flex items-center justify-between px-4 py-3 border-t border-gray-100">
          <span class="text-xs text-gray-400">{{ adminTotal }} parrain(s)</span>
          <div class="flex items-center gap-2">
            <button :disabled="adminPage <= 1" @click="loadAdmin(adminPage - 1)" class="text-sm px-2 py-1 rounded border border-gray-200 disabled:opacity-40 focus:outline-none"><i class="material-icons align-middle" style="font-size:16px;">chevron_left</i></button>
            <span class="text-sm text-gray-500">{{ adminPage }} / {{ adminLastPage }}</span>
            <button :disabled="adminPage >= adminLastPage" @click="loadAdmin(adminPage + 1)" class="text-sm px-2 py-1 rounded border border-gray-200 disabled:opacity-40 focus:outline-none"><i class="material-icons align-middle" style="font-size:16px;">chevron_right</i></button>
          </div>
        </div>
      </div>
    </div>

    <!-- ============ MODAL: filleuls d'un parrain (admin) ============ -->
    <vue-modal-2 name="parrainReferralsModal" :headerOptions="{ title: 'Filleuls du parrain' }" :noFooter="true" modalSize="lg" @on-close="$vm2.close('parrainReferralsModal')">
      <div class="bg-white rounded-lg p-4">
        <!-- En-tête parrain -->
        <div v-if="prModal.parrain" class="flex items-center justify-between flex-wrap gap-2 mb-4 pb-3 border-b border-gray-100">
          <div>
            <div class="font-bold text-gray-800">{{ prModal.parrain.fullName }} <span class="text-xs text-gray-400">({{ prModal.parrain.type }})</span></div>
            <div class="text-xs text-gray-400">{{ prModal.parrain.email }} · <span class="font-mono text-green-700">{{ prModal.parrain.referralCode }}</span></div>
          </div>
          <div class="text-right">
            <div class="text-xs text-gray-400 uppercase">Total cadeaux</div>
            <div class="font-bold text-green-700">
              {{ prModal.parrain.currency === 'USD' ? ((parseFloat(prModal.parrain.stats && prModal.parrain.stats.totalGiftUSD) || 0).toFixed(2) + ' USD') : (Math.floor((prModal.parrain.stats && prModal.parrain.stats.totalGiftMAD) || 0) + ' MAD') }}
            </div>
          </div>
        </div>

        <div v-if="prModal.loading" class="text-center py-8 text-gray-400 text-sm">Chargement...</div>
        <div v-else style="max-height:60vh;overflow-y:auto;">
          <table class="min-w-full w-full text-sm">
            <thead>
              <tr class="bg-gray-50 text-gray-500 uppercase text-xs">
                <th class="px-3 py-2 text-left">Seller (filleul)</th>
                <th class="px-3 py-2 text-left">Inscrit le</th>
                <th class="px-3 py-2 text-center">1ère expédition</th>
                <th class="px-3 py-2 text-center">Statut</th>
                <th class="px-3 py-2 text-right">Cadeau</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in prModal.referrals" :key="r._id" class="border-b border-gray-50">
                <td class="px-3 py-2">
                  <div class="font-semibold text-gray-700">{{ r.fullName }} <span class="text-xs text-gray-400">({{ r.type }})</span></div>
                  <div class="text-xs text-gray-400">{{ r.email }}</div>
                </td>
                <td class="px-3 py-2 text-gray-500">{{ r.referralDate ? $moment(r.referralDate).format('DD/MM/YY') : '-' }}</td>
                <td class="px-3 py-2 text-center">
                  <span v-if="r.firstExpeditionReceivedAt" class="text-green-600 text-xs">{{ $moment(r.firstExpeditionReceivedAt).format('DD/MM/YY') }}</span>
                  <span v-else class="text-gray-400 text-xs">—</span>
                </td>
                <td class="px-3 py-2 text-center">
                  <span class="text-xs px-2 py-0.5 rounded-full font-medium"
                        :class="r.rewardStatus === 'credited' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'">
                    {{ r.rewardStatus === 'credited' ? '🎁 Récompensé' : 'En attente' }}
                  </span>
                </td>
                <td class="px-3 py-2 text-right font-semibold" :class="r.rewardStatus === 'credited' ? 'text-green-700' : 'text-gray-400'">
                  {{ r.rewardStatus === 'credited' ? ('+ ' + (r.rewardCurrency === 'USD' ? (r.rewardAmount.toFixed ? r.rewardAmount.toFixed(2) : r.rewardAmount) + ' USD' : r.rewardAmount + ' MAD')) : '—' }}
                </td>
              </tr>
              <tr v-if="!prModal.referrals.length">
                <td colspan="5" class="px-3 py-6 text-center text-gray-400">Aucun filleul</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </vue-modal-2>

  </div>
</template>

<script>
export default {
  props: {
    currentUser: { type: Object },
    idWharhouse: { type: Object },
  },
  data() {
    return {
      loading: false,
      loaded: false,   // seller: kayn response mn getAffiliateInfo (bach ma-nbaynoش "inactif" 9bel ma-yjiو les data)
      // Seller
      info: { referralCode: null, stats: {}, rewards: [], referrals: [], gift: {}, currency: 'MAD' },
      // Admin
      adminStats: {},
      adminGift: { active: false, amountMAD: 0, amountUSD: 0 },
      adminList: [],
      adminTotal: 0,
      adminPage: 1,
      adminPerPage: 25,
      adminLastPage: 1,
      search: '',
      // Modal filleuls d'un parrain
      prModal: { loading: false, parrain: null, referrals: [] },
    };
  },
  computed: {
    isSellerPartner() {
      return this.currentUser && ['Seller', 'Partner'].includes(this.currentUser.type);
    },
    referralLink() {
      if (!this.info.referralCode) return '...';
      // Landing page (production) — pas l'app (localhost). Langue selon la locale de l'app.
      const LANDING = 'https://mlshipafrica.com';
      let lang = 'fr';
      try {
        const l = (this.$i18n && this.$i18n.locale) || (this.$root && this.$root.$i18n && this.$root.$i18n.locale) || localStorage.getItem('lang') || 'fr';
        lang = ['fr', 'en', 'ar'].includes(String(l).toLowerCase().slice(0, 2)) ? String(l).toLowerCase().slice(0, 2) : 'fr';
      } catch (e) { lang = 'fr'; }
      return `${LANDING}/${lang}/?ref=${this.info.referralCode}`;
    },
    st() { return this.info.stats || {}; },
    myGiftLabel() {
      const g = this.info.gift || {};
      return this.info.currency === 'USD'
        ? `${(parseFloat(g.amountUSD) || 0).toFixed(2)} USD`
        : `${Math.floor(g.amountMAD || 0)} MAD`;
    },
    myTotalEarned() {
      const s = this.st;
      return this.info.currency === 'USD'
        ? `${(parseFloat(s.totalGiftUSD) || 0).toFixed(2)} USD`
        : `${Math.floor(s.totalGiftMAD || 0)} MAD`;
    },
  },
  async mounted() {
    if (this.isSellerPartner) await this.loadSeller();
    else { await this.loadAdminStats(); await this.loadAdmin(1); }
  },
  methods: {
    async loadSeller() {
      this.loading = true;
      const res = await this.$server.affiliation('users', { getAffiliateInfo: true });
      this.loading = false;
      if (res && res.content && res.content.referralCode !== undefined) this.info = { ...this.info, ...res.content };
      this.loaded = true;
    },
    async regenerate() {
      this.$confirm('Générer un nouveau code ? L\'ancien lien ne fonctionnera plus.').then(async (ok) => {
        if (!ok) return;
        this.loading = true;
        const res = await this.$server.affiliation('users', { regenerateReferralCode: true });
        this.loading = false;
        if (res && res.content && res.content.referralCode) {
          this.info.referralCode = res.content.referralCode;
          this.$toast.success('Nouveau code généré');
        }
      });
    },
    copy(text) {
      if (!text) return;
      const done = () => this.$toast.success('Copié ✓');
      if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(text).then(done);
      else {
        const el = document.createElement('textarea');
        el.value = text; document.body.appendChild(el); el.select();
        document.execCommand('copy'); document.body.removeChild(el); done();
      }
    },
    async openParrainReferrals(p) {
      this.prModal = { loading: true, parrain: p, referrals: [] };
      this.$vm2.open('parrainReferralsModal');
      const res = await this.$server.affiliation('users', { getParrainReferrals: true, parrainId: p._id });
      this.prModal.loading = false;
      if (res && res.content && res.content.referrals) {
        this.prModal.parrain = res.content.parrain || p;
        this.prModal.referrals = res.content.referrals;
      }
    },
    async loadAdminStats() {
      const res = await this.$server.affiliation('users', { getAdminAffiliateStats: true });
      if (res && res.content) {
        this.adminStats = res.content.stats || {};
        this.adminGift = res.content.gift || this.adminGift;
      }
    },
    async loadAdmin(page) {
      this.loading = true;
      const res = await this.$server.affiliation('users', { getAdminAffiliates: true, search: this.search, page, limit: this.adminPerPage });
      this.loading = false;
      if (res && res.content && res.content.results) {
        this.adminList = res.content.results;
        this.adminTotal = res.content.total || 0;
        this.adminPage = res.content.current_page || page;
        this.adminLastPage = res.content.last_page || 1;
      }
    },
  },
};
</script>
